import { Tradable } from "@models/interfaces/tradable";
import { Fiat } from "@models";
import { Helper } from "./functions";
import { APPLE_MOBILE_PLATFORMS } from "@helpers/constants";
import { Timespan } from "@helpers/const/timespan";
import { Big } from "big.js";
import { ChartLineColor } from "@modules/shared/chart/models/chart-config.model";

// TODO: eslint fix
// TODO: Issue with JSON.stringify comparison: https://dmitripavlutin.com/how-to-compare-objects-in-javascript/#2-manual-comparison
/* eslint-disable  @typescript-eslint/no-explicit-any */
export function equal(object1: any, object2: any): boolean {
	return JSON.stringify(object1) === JSON.stringify(object2) && typeof object1 === typeof object2;
}

export function formatIban(iban: string): string {
	return iban ? iban.replace(/[0-9a-zA-Z]{4}/g, "$& ") : "";
}

export const isNotNullOrUndefined = <T>(value: T): boolean => value !== null && value !== undefined;

export const isNullOrUndefined = <T>(value: T): boolean => value == null || value == undefined;

export const isAppleMobilePlatform = (): boolean =>
	APPLE_MOBILE_PLATFORMS.includes(navigator.platform) ||
	(navigator.userAgent.includes("Mac") && "ontouchend" in document);

export function empty(object: any): boolean {
	return (
		object === undefined ||
		object === null ||
		object === "" ||
		object.length === 0 ||
		JSON.stringify(object) === JSON.stringify({})
	);
}

export function roundTo5(num: number, precision: number): number {
	const p = Math.pow(10, precision - 1);
	num *= p;
	num = Math.ceil(num / 5) * 5;
	return Math.ceil(num) / p;
}

export function round(input: number, precision: number): number {
	const p = Math.pow(10, precision);
	const scaledNumber = input * p;
	return Math.round(scaledNumber) / p;
}

export function stripFunnyInput(n: any): string {
	const copy = "" + n;

	if (copy.indexOf("e-") >= 0) {
		n = "0.";
		for (let i = 1; i < +copy.substring(copy.indexOf("e-") + 2); i++) {
			n += "0";
		}
		const leadingNumber = copy.substring(0, copy.indexOf("e-"));
		const dotIndex = leadingNumber.indexOf("."); // -1
		if (dotIndex >= 0) {
			n += leadingNumber.substring(0, dotIndex) + leadingNumber.substring(dotIndex + 1);
		} else {
			n += copy.charAt(0);
		}
	} else if (copy.indexOf("e+") >= 0) {
		const indexE = copy.indexOf("e+");

		n = copy.charAt(0) + copy.substring(2, indexE - 1);
		const L = n.length;
		for (let i = L; i <= +copy.substring(copy.indexOf("e+") + 2); i++) {
			n += "0";
		}
	}
	n = "" + n;
	n = n.replace(/\s/g, "");

	if (isNaN(+n)) {
		if (getCurrentDecimalSeperator() === ".") {
			n = n.replace(/,/g, "");
		} else {
			n = n.replace(/\./g, "").replace(",", ".");
		}

		// Still not a number and it's only the decimal separator
		if (isNaN(+n) && n === ".") {
			n = "0";
		}
	}

	return n;
}

export function truncate(num: number, places: number): number {
	const n = stripFunnyInput(num);
	const vk = n.split(".")[0].length;
	return +n.substr(0, vk + places + 1);
}

export function isEmail(email: string): boolean {
	const pattern: RegExp = new RegExp(
		/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[([0-9]{1,3}\.){4}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
	);
	return pattern.test(email);
}

export function truncateFixed(amount: number, precision: number): string {
	return truncate(amount, precision).toLocaleString([], {
		localeMatcher: "lookup",
		minimumIntegerDigits: 1,
		minimumFractionDigits: precision
	});
}

export function formatTimeSpan(remaining: number): string[] {
	const time = [];
	time.push(n0(remaining % 60) + "S");
	remaining = Math.floor(remaining / 60);
	time.push(n0(remaining % 60) + "M");
	remaining = Math.floor(remaining / 60);
	time.push(n0(remaining % 24) + "H");
	remaining = Math.floor(remaining / 24);
	time.push(n0(remaining) + "D");
	return time;
}

export const matchesMediaQuery = (size: number): boolean => window.matchMedia(`(min-width: ${size}px)`).matches;

/*
 * https://stackoverflow.com/a/11268104
 */
export function scorePassword(pwd: string): number {
	const letters = {};
	let check;
	let ltr;
	let i;
	let l;
	let variation = 0;
	let score = 0;

	if (!pwd) {
		return score;
	}

	/* Score character variation */
	const variations = {
		lower: /[a-z]/.test(pwd),
		upper: /[A-Z]/.test(pwd),
		nonWords: /\W/.test(pwd),
		digits: /\d/.test(pwd)
	};

	for (check in variations) {
		if (variations.hasOwnProperty(check)) {
			variation += variations[check] ? 1 : 0;
		}
	}

	score += (variation - 1) * 10;

	/* Score unique letters until 5 repetitions */
	for (i = 0, l = pwd.length; i < l; i++) {
		ltr = letters[pwd[i]] = (letters[pwd[i]] || 0) + 1;
		score += 5 / ltr;
	}

	/* Score length (about 8 chars for a safe password) */
	score -= 16 - pwd.length / 16;

	return parseInt("" + score, 0);
}

export function colorPassword(i: number, pwd: string): string {
	const def = "#DDD";
	let color = "red";
	let score = scorePassword(pwd);

	if (pwd.length === 0) {
		return def;
	}

	// Convert password length to a scale of 0 to 5
	score = Math.round(pwd.length / 5);

	// Assign a color according to the score
	if (score === 3) {
		color = "orange";
	} else if (score > 3) {
		color = "green";
	}

	// Return dark gray or the color depending on each index
	if (i > score) {
		return def;
	}

	return color;
}

export function isFunction(functionToCheck): boolean {
	return functionToCheck && {}.toString.call(functionToCheck) === "[object Function]";
}

export function hasNumber(element: string): boolean {
	return /\d/.test(element);
}

// TODO: eslint fix (Change to lodash cloneDeep) https://www.blazemeter.com/blog/the-correct-way-to-import-lodash-libraries-a-benchmark
export function deepCopy(obj: any, T: any = null): any {
	if (undefined === obj || null === obj || "object" !== typeof obj) {
		return obj;
	}

	let copy = null;

	if (obj instanceof Date) {
		copy = new Date();
		copy.setTime(obj.getTime());
		return copy;
	}

	if (obj instanceof Array) {
		return obj.map((item) => deepCopy(item, T));
	}

	if (obj instanceof Object) {
		if (obj instanceof T) {
			copy = new T();
		} else {
			copy = {};
		}

		for (const attrName in obj) {
			if (obj.hasOwnProperty(attrName)) {
				copy[attrName] = deepCopy(obj[attrName], T);
			}
		}
		return copy;
	}
	return copy; // Default null
}

export function roundToNextCeiling(balance: number, ceiling: number): number {
	return Math.ceil(balance / ceiling) * ceiling;
}

export function getLocaleArray(source: string): string[] {
	if (source.toLowerCase().startsWith("de")) {
		return ["de-DE", "en-GB", "fr-FR"];
	}
	if (source.toLowerCase().startsWith("fr")) {
		return ["fr-FR", "en-GB", "de-DE"];
	}
	if (source.toLowerCase().startsWith("sp")) {
		return ["es-ES", "en-GB", "de-DE"];
	}
	if (source.toLowerCase().startsWith("po")) {
		return ["pl-PL", "en-GB", "de-DE"];
	}
	if (source.toLowerCase().startsWith("tu")) {
		return ["tr-TR", "en-GB", "de-DE"];
	}
	if (source.toLowerCase().startsWith("it")) {
		return ["it-IT", "en-GB", "de-DE"];
	}
	if (source.toLowerCase().startsWith("ron")) {
		return ["ron-RO", "en-GB", "de-DE"];
	}
	if (source.toLowerCase().startsWith("hrv")) {
		return ["hrv-HR", "en-GB", "de-DE"];
	}
	if (source.toLowerCase().startsWith("hun")) {
		return ["hu-HU", "en-GB", "de-DE"];
	}
	if (source.toLowerCase().startsWith("ces")) {
		return ["ces-CS", "en-GB", "de-DE"];
	}

	return ["en-GB", "de-DE", "fr-FR", "es-ES", "pl-PL", "tr-TR", "it-IT", "ron-RO", "hrv-HR", "ces-CS"];
}

export function getCurrentDecimalSeperator(): string {
	let n = 1.1;
	return n.toLocaleString(getLocaleArray(Helper.getLanguage())).substring(1, 2);
}

export function sort(a: boolean, b: boolean): number {
	if (a) {
		return -1;
	}
	if (b) {
		return 1;
	}
	return 0;
}

export function isSafari(): boolean {
	const ua = navigator.userAgent.toLowerCase();

	if (ua.indexOf("safari") !== -1) {
		return ua.indexOf("chrome") <= -1;
	}
	return false;
}

export function n0(n: number): string {
	if (n < 10) {
		return "0" + n;
	}
	return "" + n;
}

export function stringStartsWith(s1: string, s2: string): boolean {
	return s1.toLowerCase().startsWith(s2.toLowerCase());
}

export function stringIncludes(s1: string, s2: string): boolean {
	return s1.toLowerCase().includes(s2.toLowerCase());
}

export function containsString(itemValue: string, searchInput: string): boolean {
	if (searchInput.length === 1) {
		if (stringStartsWith(itemValue, searchInput)) {
			return true;
		}
	} else if (stringIncludes(itemValue, searchInput)) {
		return true;
	}
	return false;
}

export function isVerticalScrollbarClick(event: any): boolean {
	return event.offsetX >= event.target.clientWidth;
}

export function divideArrayInChunks<T>(arr: T[], size: number): T[][] {
	return [...Array(Math.ceil(arr.length / size))].map((_, i) => arr.slice(size * i, size + size * i));
}

export function getPriceChange(asset: Tradable, timespan: Timespan): { changePercent: number; changeAmount: number } {
	switch (timespan) {
		case Timespan.Day:
			return {
				changePercent: asset.getChange24h(),
				changeAmount: asset.getChange24hAmount()
			};
		case Timespan.Week:
			return {
				changePercent: asset.getChange1w(),
				changeAmount: asset.getChange1wAmount()
			};
		case Timespan.Month:
			return {
				changePercent: asset.getChange1m(),
				changeAmount: asset.getChange1mAmount()
			};
		case Timespan.SixMonths:
			return {
				changePercent: asset.getChange6m(),
				changeAmount: asset.getChange6mAmount()
			};
		case Timespan.Year:
			return {
				changePercent: asset.getChange1y(),
				changeAmount: asset.getChange1yAmount()
			};
		case Timespan.FiveYears:
			return {
				changePercent: asset.getChange5y(),
				changeAmount: asset.getChange5yAmount()
			};
		default:
			console.error("Unknown timespan");
	}
}

export function getLineColor(performance: number): ChartLineColor {
	let color = ChartLineColor.Green;
	if (performance < 0) {
		color = ChartLineColor.Red;
	} else if (performance === 0) {
		color = ChartLineColor.Gray;
	}
	return color;
}

export function euroToDefault(amount: number, defaultFiat: Fiat): number {
	return amount / defaultFiat.toEuroRate();
}

export function changeTokenUnit(num: string | number): string {
	num = +num; // Cast to number so the comparison works

	if (num % 1 !== 0 && num < 2) {
		return "fma_unit_up_two";
	}

	if (num % 1 !== 0 && num > 2) {
		return "fma_unit_two";
	}

	if (num === 0) {
		return "fma_unit_zero";
	}

	if (num === 2 || num === 3 || num === 4) {
		return "fma_unit_two";
	}

	return num === 1 ? "fma_unit_one" : "fma_unit_other";
}

/**
 *
 * @param oldArray - this param is used to determine if the property is new or already exists
 * @param newArray - new Array will be merged into the old one
 * @param key - to check if the prop exists & also to read the value
 */
export function updateArray<T>(oldArray: T[], newArray: T[], key: string): T[] {
	const oldArrayMap = oldArray.reduce((map, item) => {
		map[item[key]] = item;
		return map;
	}, {});

	// Iterate over the new array
	newArray.forEach((newItem) => {
		oldArrayMap[newItem[key]] = newItem;
	});

	return Object.values(oldArrayMap);
}

export function setPrecisionDecimals(value: string, precision: number): string {
	return new Big(value).toFixed(precision, 0);
}

export function stringToNumberWithPrecision(value: string, fractionDigits: number): number {
	return parseFloat(`${value}`.match(new RegExp(`^-?\\d+(?:\.\\d{0,${fractionDigits}})?`))[0]);
}

export function maskMobilePhoneNumber(mobile: string, visibleDigits: number = 3): string {
	if (mobile) {
		// Split everything into an array
		const mobileArray = mobile.split("");
		const visibleLength = Math.min(visibleDigits, mobile.length);

		// First check if there are any chars to be masked
		// If no, then return the number
		if (visibleLength === mobile.length) {
			return mobile;
		}

		// If yes loop through it

		// Avoiding for loop as eslint does not quite like that
		// and for-of / .forEach do not allow replacing
		let index = mobile.length - visibleLength;
		while (index--) {
			// Test if the item is a digit
			if (/\d/.test(mobileArray[index])) {
				mobileArray[index] = "*";
			}
		}
		// Finally add a space if it does not exist to fix the CSS bug
		if (mobileArray[mobileArray.length - visibleLength - 1] !== " ") {
			mobileArray.splice(mobileArray.length - visibleLength, 0, " ");
		}
		return mobileArray.join("");
	}
	return "";
}

export function fixToPrecisionHelper(val: number = 0, precision: number): number {
	const value = new Big(val);
	const result = value.toFixed(precision);
	return +result;
}

export function stripToDecimals(num: number, decimals: number): number {
	const bigNum = new Big(num);
	const factor = new Big(10).pow(decimals);
	const truncated = bigNum.times(factor).round(0, 0).div(factor); // 0 = ROUND_DOWN
	return +truncated.toString();
}

export function bumpLastDecimal(value: number, decimals: number = 8): number {
	const str = value.toFixed(decimals);
	// Find the last digit and bump it by 2
	const parts = str.split(".");
	if (parts.length === 2 && parts[1].length === decimals) {
		const decArr = parts[1].split("");
		const lastDigit = parseInt(decArr[decArr.length - 1], 10);
		decArr[decArr.length - 1] = ((lastDigit + 1) % 10).toString();
		return Number(`${parts[0]}.${decArr.join("")}`);
	}
	return value;
}
